import { useCallback, useMemo } from 'react'
import { isEqual, optionHelpers } from 'shared-utils/utils'
import uniqBy from 'lodash/uniqBy'
import type {
  FilterOptionDTO,
  OldProductDTO,
  Option,
  UIVariant,
  UIVariantOption,
  UIVariantOptionGroup,
  UseVariantHook
} from 'ecosystem'
import { getProductImages } from 'shared-utils/utils/products'
import { useDispatch, useSelector } from 'shared-redux'
import { selectedOptionsState, setSelectedOptions } from 'shared-redux/state'
import { useProductContext } from './useProductContext'

const useVariants = (variantGroupId: Option<string>, isFilterByStock?: boolean): UseVariantHook => {
  const { variants: variantsByIds } = useProductContext()
  const dispatch = useDispatch()
  const selected = useSelector(selectedOptionsState)
  const { paths } = useProductContext()

  const { data: variants, error } = useMemo(() => {
    const result = variantGroupId ? variantsByIds?.[variantGroupId] : null
    return result ?? { data: null }
  }, [variantGroupId, variantsByIds])

  const uiVariants: Option<UIVariant[]> = useMemo(() => {
    return (
      variants
        ?.filter((v) => (isFilterByStock ? v.inStock : true))
        .map((e) => uiVariantMapping(e, paths.productsUrlPath)) ?? null
    )
  }, [variants, isFilterByStock, paths.productsUrlPath])

  const unavailableUiVariants: Option<UIVariant[]> = useMemo(() => {
    return (
      variants
        ?.filter((v) => (isFilterByStock ? !v.inStock : false))
        .map((e) => uiVariantMapping(e, paths.productsUrlPath)) ?? null
    )
  }, [variants, isFilterByStock, paths.productsUrlPath])

  const optionGroups = useMemo((): UIVariantOptionGroup[] => {
    if (!uiVariants || uiVariants.length < 2) {
      return []
    }

    const allOptions = uiVariants.flatMap((uiVariant) => uiVariant.optionGroups)

    const groupedOptions = uniqBy(allOptions, 'id')
      .filter((_v) => _v.variant)
      .map((v) => {
        const values = allOptions.filter((_v) => _v.id === v.id).flatMap((_v) => _v.options)
        const options: UIVariantOption[] = uniqBy(values, 'value').map((o) => ({
          groupId: v.id,
          value: o.value
        }))
        return {
          ...v,
          options
        }
      })

    if (groupedOptions.length < 2) {
      return []
    }

    return groupedOptions
  }, [uiVariants])

  const filteredVariants = useMemo(() => {
    return (
      uiVariants?.filter((variant) =>
        selected.every((selectedOption) =>
          variant.optionGroups.find((optionGroup) =>
            optionGroup.options.find((o) => isEqual(o, selectedOption))
          )
        )
      ) ?? null
    )
  }, [selected, uiVariants])

  const selectOption = useCallback(
    (option: UIVariantOption) => {
      dispatch(setSelectedOptions({ option }))
    },
    [dispatch]
  )

  const isOptionSelected = useCallback(
    (option: UIVariantOption) => {
      const { optionFinder } = optionHelpers(option)
      return Boolean(selected.find(optionFinder))
    },
    [selected]
  )

  return {
    variants: filteredVariants,
    unavailableUiVariants,
    optionGroups,
    selectOption,
    isOptionSelected,
    error
  }
}

export default useVariants

const uiVariantMapping = (product: OldProductDTO, productsUrlPath: string): UIVariant => {
  const { mainImageUrl } = getProductImages(product.medias)
  return {
    ...product,
    path: `${productsUrlPath}/${product.slug}`,
    imageUrl: mainImageUrl,
    optionGroups: product.options?.map(uiVariantOptionMapping) || [],
    variantGroupId: product.variantGroup,
    isMain: product.main
  }
}

const uiVariantOptionMapping = (o: FilterOptionDTO): UIVariantOptionGroup => {
  const label = o.label ? ` ${o.label}` : ''
  const values = o.textValues || o.numberValues?.map((v) => `${v}${label}`) || []

  return {
    id: o.name,
    displayName: `${o.name}${label}`,
    variant: o.variant,
    options: values.map((v) => ({
      groupId: o.name,
      value: v
    }))
  }
}
